import {AfterContentInit, ChangeDetectionStrategy, Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {
  ApDynComponentComponent
}                                                                                           from '../../../ap-dyncomponent/ap-dyncomponent.component';
import {
  ApDynGridColumnConfigBase
}                                                                                           from '../../../ap-dyngrids/config/ap-dyn-grid-column-config-base';
import {
  ApDynGridPagerConfigBase
}                                                                                           from '../../../ap-dyngrids/config/ap-dyn-grid-pager-config-base';
import {
  ApDynGridPropertyColumnConfig,
  PropertyTemplate
}                                                                                           from '../../../ap-dyngrids/config/ap-dyn-grid-property-column-config';
import {
  ApDynGridStatusColumnConfig
}                                                                                           from '../../../ap-dyngrids/config/ap-dyn-grid-status-column-config';
import {
  ApDynGridPagerWizardConfig,
  ApDynGridPagerWizardSection
}                                                                                           from '../../../ap-dyngrids/config/ap-dyn-grid-pager-wizard-config';
import {
  ApDynGridEditColumnConfig
}                                                                                           from '../../../ap-dyngrids/config/ap-dyn-grid-edit-column-config';
import {debounceTime, map}                                                                  from 'rxjs/operators';
import {
  IGridData
}                                                                                           from '../../../ap-interface';
import {
  MapViewStore
}                                                                                           from '../../../stores/layout/mapview.store';
import {
  TranslationStore
}                                                                                           from '../../../stores/translation/translation.store';
import {
  ApDateService,
  DateDiffType
}                                                                                           from '../../../ap-core/services/ap-date-service';
import {combineLatest, Observable, of, Subscription}                                        from 'rxjs';
import {
  ApUtilService
}                                                                                           from '../../../ap-utils/service/ap-util.service';
import {
  LicenseStore
}                                                                                           from '../../../stores/farm/license.store';
import {Create, ObjectFactory}                                                              from 'ts-tooling';
import {
  LoginStore
}                                                                                           from '../../../stores/login/login.store';
import {
  ApRoleTypeService
}                                                                                           from '../../../services/common/ap-role-type.service';
import {
  FieldStore
}                                                                                           from '../../../stores/farm/field.store';
import {
  SoilSampleDates,
  SoilSampleDateStore
}                                                                                           from '../../../stores/nutrients/soilsampledate.store';
import {
  SoilSampleDateService
}                                                                                           from '../../../services/data/soil-sample-date.service';
import {
  IGridStatus
}                                                                                           from '../../../ap-interface/interfaces/ap-grid-status.interface';
import {
  ApColumnState
}                                                                                           from '../../../ap-interface/enums/ap-column-state.enum';
import {
  ApFeatureModulKey
}                                                                                           from '../../../ap-interface/enums/ap-feature-modul-key.enum';
import ILicense = Data.Licensing.ILicense;
import IField = Data.FieldManagement.IField;

interface ILicenseGridData extends IGridData<ILicense> {
  Status: number;
  StatusClass: string;
  StatusDesc: string;
  Module: any;
  ValidFrom: any;
  ValidTo: any;
  TrialExpiredAt: any;
  NoCheck: boolean;
  UsedAmount: number | undefined;
  LicensedAmount: number | undefined;
}

@Component({
  selector: 'ap-licence',
  template: `
    <ap-dyncomponent [caption]="'Global__License_Overview'"
                     [canSearch]="true"
                     [headerIcon]="'ap-icon-settings'"
                     [filterTooltip]="'filterTooltip'"
                     [columns]="columns"
                     [items]="items$ | async"
                     [loading$]="loading$"
                     [pager]="pager"
                     [selectable]="{checkboxOnly: false, mode: 'multiple'}"
    ></ap-dyncomponent>`,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ApLicenceComponent implements OnInit, AfterContentInit, OnDestroy {
  @ViewChild(ApDynComponentComponent, {static: true}) dynComponent: ApDynComponentComponent;
  public loading$ = of(true);
  public items$: Observable<ILicenseGridData[]> = of([]);
  public columns: ApDynGridColumnConfigBase[] = [];
  public pager: ApDynGridPagerConfigBase;

  private _isSaving = false;
  private _subscriptions: Subscription[] = [];

  constructor(private translationStore: TranslationStore,
              private mapViewStore: MapViewStore,
              private loginStore: LoginStore,
              private licenseStore: LicenseStore,
              private fieldStore: FieldStore,
              private soilSampleDateStore: SoilSampleDateStore,
              private soilSampleDateService: SoilSampleDateService,
              private dateService: ApDateService) {
  }

  public ngOnInit(): void {
    this.loading$ = combineLatest([
      this.fieldStore.Loading$,
      this.licenseStore.Loading$,
      this.soilSampleDateStore.Loading$
    ]).pipe(
      map(loadingItems => loadingItems.some(loading => loading))
    );
    this.mapViewStore.hideMapView();
    this.columns = this._getColumns();
    this.pager = this._getPager();
  }

  public ngAfterContentInit(): void {
    this._subscriptions.push(
      combineLatest([
        this.licenseStore.Listen(x => x.saved),
        this.licenseStore.Listen(x => x.saving)
      ]).pipe(
        debounceTime(100)
      ).subscribe(([saved, saving]) => {
        this._isSaving = !((!saved && !saving) || (saved && !saving));
      }));
    this.items$ = this._getItems();
  }

  public ngOnDestroy(): void {
    this._subscriptions.forEach(x => x.unsubscribe());
  }

  /**
   * Retrieves license items with additional processing
   * Transforms license data by:
   * - Filtering licenses based on user role
   * - Calculating license status
   * - Formatting dates
   * - Adding additional metadata
   * @returns Observable of processed license grid data
   * @private
   */
  private _getItems(): Observable<ILicenseGridData[]> {
    return combineLatest([
      this.licenseStore.licenseData$,
      this.fieldStore.Fields$,
      this.soilSampleDateStore.Dates$
    ]).pipe(
      map(([licenses, fields, soilSampleDates]) => {
        let filteredLicenses = licenses;
        if (!ApRoleTypeService.hasDeveloperRole(this.loginStore.User)) {
          filteredLicenses = licenses.filter(x => !!x.FeatureModul && x.FeatureModul?.ErpId > 0);
        }
        return filteredLicenses.map((license: ILicense) => {
          const validFromDate = this._getDateForLicense(license.ValidFrom, undefined);
          const validToDate = this._getDateForLicense(license.ValidTo, new Date(2100, 1));
          const trialExpiredAt = this._getDateForLicense(license.TrialExpireAt, undefined);
          const gridStatus = this._getLicenseStatusObject(license, validToDate);
          return {
            Id: license.Id,
            SourceItem: license,
            Status: gridStatus.state,
            StatusClass: gridStatus.class,
            StatusDesc: gridStatus.description,
            Module: this.translationStore.FindTranslationForSelectedLanguage(license.FeatureModul.Translationkey),
            ValidFrom: validFromDate,
            ValidTo: validToDate,
            TrialExpiredAt: trialExpiredAt,
            UsedAmount: this._getUsedAmount(license, fields, soilSampleDates),
            LicensedAmount: license?.QuantityErp > 0 ? license.QuantityErp : undefined,
            NoCheck: license.NoCheck
          };
        });
      })
    );
  }

  /**
   * Generates grid columns for the license overview
   * Configures column settings including:
   * - Status column
   * - Module column
   * - Amount columns
   * - Date columns
   * - Edit actions
   * @returns Array of column configurations
   * @private
   */
  private _getColumns(): ApDynGridColumnConfigBase[] {
    return [
      new ApDynGridStatusColumnConfig({
        field: 'Status',
        showHeaderTitle: false,
        class: 'StatusClass',
        description: 'StatusDesc',
        filterable: true
      }),
      new ApDynGridPropertyColumnConfig({
        field: 'Module',
        title: 'Global__Module',
        filterable: true,
        headerFilterable: true,
        sortIndex: 1,
        sortDesc: false,
        width: 200
      }),
      new ApDynGridPropertyColumnConfig({
        field: 'UsedAmount',
        title: 'UsedAmount',
        template: PropertyTemplate.NUMBER,
        width: 90,
        filterable: true
      }),
      new ApDynGridPropertyColumnConfig({
        field: 'LicensedAmount',
        title: 'LicensedAmount',
        template: PropertyTemplate.NUMBER,
        width: 90,
        filterable: true
      }),
      new ApDynGridPropertyColumnConfig({
        field: 'ValidFrom',
        title: 'Docu_Ini__From',
        format: 'DD.MM.YYYY',
        width: 90
      }),
      new ApDynGridPropertyColumnConfig({
        field: 'ValidTo',
        title: 'Docu_Ini__To',
        format: 'DD.MM.YYYY',
        width: 90
      }),
      new ApDynGridPropertyColumnConfig({
        field: 'TrialExpiredAt',
        title: 'TrialExpiredAt',
        format: 'DD.MM.YYYY',
        tooltip: (item: ILicenseGridData) => this._generateTooltipForTrialExpiredAt(item),
        width: 90
      }),
      new ApDynGridPropertyColumnConfig({
        title: 'No_Check',
        field: 'NoCheck',
        checkbox: true,
        hide: true,
        width: 40
      }),
      new ApDynGridEditColumnConfig({
        data: [
          {
            text: (item: ILicenseGridData) => this.translationStore.FindTranslationForSelectedLanguage(
              item.NoCheck ? 'Enable_Check' : 'Disable_Check'
            ),
            callback: ApUtilService.createEventEmitter(this._switchLicenseCheck.bind(this), this._subscriptions),
            disabled: !ApRoleTypeService.hasAgriconRole(this.loginStore.User) || this._isSaving
          },
          {
            text: this.translationStore.FindTranslationForSelectedLanguage('Global__activate_trial_license'),
            callback: ApUtilService.createEventEmitter(this._activateTrialLicense.bind(this), this._subscriptions),
            disabled: (item: ILicenseGridData) => item.Status !== 0 || this._isSaving || this.checkIsTrialDateIsValid(item)
          },
          {
            text: this.translationStore.FindTranslationForSelectedLanguage('ResetTrialLicense'),
            callback: ApUtilService.createEventEmitter(this._resetTrialLicense.bind(this), this._subscriptions),
            disabled: () => !ApRoleTypeService.hasAgriconRole(this.loginStore.User) || this._isSaving
          }
        ]
      })
    ];
  }

  /**
   * Creates a pager configuration for the license grid
   * Sets up empty wizard pager sections
   * @returns Pager configuration
   * @private
   */
  private _getPager(): ApDynGridPagerWizardConfig {
    return new ApDynGridPagerWizardConfig(
      new ApDynGridPagerWizardSection(),
      new ApDynGridPagerWizardSection(),
      new ApDynGridPagerWizardSection()
    );
  }

  /**
   * Processes a license date, applying default values if needed
   * @param date Original date from license
   * @param toReplace Replacement date if original is undefined
   * @returns Processed date at midnight
   * @private
   */
  private _getDateForLicense(date: Date | undefined, toReplace: Date | undefined): Date | undefined {
    const newDate = date ? this.dateService.dateToMoment(date).toDate() : toReplace;
    if (!newDate) {
      return undefined;
    }
    return this.dateService.getDateMidnight(newDate);
  }

  /**
   * Calculates used amount for base fertilization licenses
   * Computes total area of fields with valid soil sample dates
   * @param license Current license
   * @param fields Available fields
   * @param soilSampleDates Soil sample dates
   * @returns Used amount or undefined
   * @private
   */
  private _getUsedAmount(license: ILicense, fields: IField[], soilSampleDates: SoilSampleDates): number | undefined {
    if (license.FeatureModul.Key !== ApFeatureModulKey.BaseFertilization) {
      return undefined;
    }
    const fieldsWithValidSoilSample = this.soilSampleDateService.getFieldsWithValidSoilSampleDate(fields, soilSampleDates);
    return fieldsWithValidSoilSample.reduce((totalAreaAmount, field) => {
      const fieldGeom = this.fieldStore.getCurrentFieldGeom(field);
      return totalAreaAmount + Create(fieldGeom?.AreaHa, 0);
    }, 0);
  }

  /**
   * Switches the license check status
   * @param item The license grid data item to toggle
   * @private
   */
  private _switchLicenseCheck(item: ILicenseGridData): void {
    if (this._isSaving) {
      return;
    }
    const copiedObject = ObjectFactory.Copy(item.SourceItem);
    copiedObject.NoCheck = !copiedObject.NoCheck;
    this.licenseStore.updateLicense(copiedObject, false, false);
  }

  /**
   * Activates a trial license
   * Sets trial expiration date to one month from current date
   * @param item The license grid data item to activate
   * @private
   */
  private _activateTrialLicense(item: ILicenseGridData): void {
    if (this._isSaving) {
      return;
    }
    const copiedObject = ObjectFactory.Copy(item.SourceItem);
    let trialExpireAt = new Date();
    trialExpireAt.setMonth(trialExpireAt.getMonth() + 1);
    trialExpireAt = this.dateService.toFarmDate(this.dateService.getDateNoon(trialExpireAt)).toDate();
    copiedObject.TrialExpireAt = trialExpireAt;
    this.licenseStore.updateLicense(copiedObject, true, false);
  }

  /**
   * Resets the trial license
   * Clears the trial expiration date
   * @param item The license grid data item to reset
   * @private
   */
  private _resetTrialLicense(item: ILicenseGridData): void {
    if (this._isSaving) {
      return;
    }
    const copiedObject = ObjectFactory.Copy(item.SourceItem);
    copiedObject.TrialExpireAt = null;
    this.licenseStore.updateLicense(copiedObject, false, true);
  }

  /**
   * Determines the status of a license
   * Evaluates license validity based on:
   * - Expiration date
   * - Number of trial activations
   * @param license License to evaluate
   * @param validTo Expiration date
   * @returns Grid status object
   * @private
   */
  private _getLicenseStatusObject(license: ILicense, validTo: Date): IGridStatus {
    const gridStatus: IGridStatus = {
      state: ApColumnState.Yellow,
      class: 'ap-status-yellow',
      description: 'Global_no_valid_license'
    };
    const currentDate = this.dateService.getDateMidnight(new Date());
    const dateDifferenceInDays = this.dateService.getDateDiff(validTo, currentDate, DateDiffType.Days);
    if (validTo && dateDifferenceInDays >= 0) {
      gridStatus.state = ApColumnState.Green;
      gridStatus.class = 'ap-status-green';
      gridStatus.description = 'Global_valid_license';
      return gridStatus;
    }
    if (license.TrialActivationsCount >= 3) {
      gridStatus.state = ApColumnState.Red;
      gridStatus.class = 'ap-status-red';
      gridStatus.description = 'Global_trials_exceeded';
      return gridStatus;
    }
    return gridStatus;
  }

  /**
   * Generates a tooltip for trial expiration date
   * Creates a formatted HTML tooltip showing:
   * - Days left until trial expiration
   * - Number of trial activations
   * @param gridItem License grid data item
   * @returns HTML tooltip string or undefined
   * @private
   */
  private _generateTooltipForTrialExpiredAt(gridItem: ILicenseGridData): string | undefined {
    if (!gridItem.TrialExpiredAt) {
      return undefined;
    }
    const trialCount = gridItem.SourceItem.TrialActivationsCount ?? '';
    let daysLeft = this.dateService.getDateDiff(gridItem.TrialExpiredAt,
      this.dateService.getUtc(), DateDiffType.Days);
    if (daysLeft < 0) {
      daysLeft = 0;
    }
    const daysLeftTranslation = this.translationStore.FindTranslationForSelectedLanguage('DaysLeft');
    const trialCountTranslation = this.translationStore.FindTranslationForSelectedLanguage('TrialCount');
    let fieldsTooltip = '<div style="display: grid; grid-template-rows: auto auto;">';
    fieldsTooltip += `<div style="grid-row: 1">${daysLeftTranslation}: ${daysLeft}</div>`;
    fieldsTooltip += `<div style="grid-row: 2">${trialCountTranslation}: ${trialCount}</div>`;
    fieldsTooltip += '</div>';
    return fieldsTooltip;
  }

  /**
   * Checks if trial date is still valid
   * @param gridItem The license grid data item to check
   * @returns Boolean indicating if trial date is valid
   * @private
   */
  private checkIsTrialDateIsValid(gridItem: ILicenseGridData): boolean {
    if (!gridItem.TrialExpiredAt) {
      return false;
    }
    const daysDifference = this.dateService.getDateDiff(new Date(gridItem.TrialExpiredAt),
      new Date(), DateDiffType.Days);
    return daysDifference >= 0;
  }
}
